import { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { getInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { Empty } from '../components/empty';
import { Result } from '../components/result/result';
import { useCalculatorConditionalLogic } from '../conditional-logic-context';
import { CTA } from './cta';
import { Footer } from './footer';
import { Header } from './header';
import { ResultsToolbar } from './results-toolbar';

const RESULTS_TOP_PADDING_WITH_BUTTONS = 60;

type ResultsProps = {
  penetration: number[];
  paddings: number[];
  omitBorderRadius: boolean;
  sticky: boolean;
  isMobile?: boolean;
};

export function Results({
  penetration,
  paddings,
  omitBorderRadius,
  sticky,
  isMobile
}: ResultsProps) {
  const {
    resultBackgroundColor,
    resultHeading,
    resultShowPrintButton,
    resultShowDownloadButton
  } = useSettings();
  const { displayedCalculations } = useCalculatorConditionalLogic();
  const visibleResults = displayedCalculations.filter(({ visible }) => visible);

  const shouldIncreaseTopPadding =
    !resultHeading && (resultShowPrintButton || resultShowDownloadButton);

  const adjustedPaddings = useMemo(() => {
    if (!shouldIncreaseTopPadding || paddings.length === 0) {
      return paddings;
    }

    const [_, ...rest] = paddings;

    return [RESULTS_TOP_PADDING_WITH_BUTTONS, ...rest];
  }, [paddings, shouldIncreaseTopPadding]);

  return (
    <Container
      _color={getInvertedColor(
        resultBackgroundColor,
        ['rgb(255, 255, 255)', 'rgb(17, 17, 17)'],
        180
      )}
      _backgroundColor={resultBackgroundColor}
      _penetration={penetration}
      _omitBorderRadius={omitBorderRadius}
      _sticky={sticky}
      _paddings={adjustedPaddings}
    >
      <ResultsToolbar />

      <Header isMobile={isMobile} />

      {visibleResults.length === 0 ? (
        <Empty _textColor={getInvertedColor(resultBackgroundColor)}>
          No results to show
        </Empty>
      ) : (
        visibleResults.map((data) => (
          <Result key={data.id} calculation={data} />
        ))
      )}

      <CTA isMobile={isMobile} />
      <Footer />
    </Container>
  );
}

const Container = styled.div<{
  _color: string;
  _sticky: boolean;
  _backgroundColor: string;
  _penetration: number[];
  _omitBorderRadius: boolean;
  _paddings: number[];
}>`
  position: relative;
  height: fit-content;
  display: flex;
  flex-direction: column;
  gap: 24px;
  color: ${({ _color }) => _color};
  background: ${({ _backgroundColor }) => _backgroundColor};
  padding: ${({ _paddings }) => _paddings.map((v) => `${v}px`).join(' ')};
  border-radius: 16px;
  ${({ _omitBorderRadius }) =>
    _omitBorderRadius &&
    css`
      border-radius: 0;
    `}
  ${({ _sticky, _penetration: [x, y] }) =>
    _sticky
      ? css`
          position: sticky;
          top: 40px;
          bottom: 40px;
        `
      : css`
          left: -${x}px;
          right: -${x}px;
          width: calc(100% + ${2 * x}px);
          margin-top: -${y}px;
          bottom: -${y}px;
        `};
`;
